<template>
  <div>
    <div class="tw-flex tw-justify-between">
      <div class="tw-text-xl tw-font-light tw-my-auto">{{ capitalize(organization.customerLabelSingular) }} Charges</div>
      <div class="tw-flex">
        <base-button type="text" size="sm" @click="openFilters()">
          <template #icon>
            <filter-icon />
          </template>
          Filters
        </base-button>
        <export-button
          class="tw-ml-5"
          size="sm"
          type="primary"
          label="Export"
          :filename="`PredictileCharges.csv`"
          :requestPayload="requestPayload"
          :url="`/businesses/${businessId}/reports/charges/export`"
          @onError="didErrorExport"
        >
        </export-button>
      </div>
    </div>
    <filter-display v-if="showFilters === true" class="tw-my-3" :filters="filters" />
    <div class="tw-mt-6">{{ page.total }} charges</div>
    <base-table :loading="isLoading" :fields="fields" :items="charges" :sort-key.sync="sortKey" :sort-desc.sync="sortDesc" :loadingRows="5" class="tw-mt-2">
      <template #date="{item: charge}">
        <div class="tw-whitespace-nowrap">{{ formatDate(charge.transactionDate) }}</div>
      </template>
      <template #amount="{item: charge}">
        <Money-Format :value="charge.amountInCents" :locale="locale" :currency-code="charge.currency" subunits-value />
      </template>
      <template #status="{item: charge}">
        <base-badge size="sm" :color="statusColor(charge.status)" class="tw-whitespace-nowrap">{{ capitalize(charge.status) }}</base-badge>
      </template>
      <template #location="{item: charge}">
        <div class="tw-whitespace-nowrap">{{ charge.businessLocationName }}</div>
      </template>
      <template #plan="{item: charge}">
        <div class="tw-whitespace-nowrap">{{ charge.businessPlanName }}</div>
      </template>
      <template #group="{item: charge}">
        <div class="tw-whitespace-nowrap">{{ charge.planGroup }}</div>
      </template>
      <template #description="{item: charge}">
        <div class="tw-whitespace-nowrap">
          <base-button type="text" @click="view(charge.userId)">{{ charge.fullName }}</base-button
          >: {{ charge.description }}
        </div>
      </template>
      <template #empty-state>
        <div class="tw-flex tw-justify-center tw-py-5">
          No Results
        </div>
      </template>
      <template #loading-row>
        <td class="tw-py-3 tw-px-4">
          <base-skeleton-loader class="tw-h-3 tw-w-24" />
        </td>
        <td class="tw-py-3 tw-px-4">
          <base-skeleton-loader class="tw-h-3 tw-w-12" />
        </td>
        <td class="tw-py-3 tw-px-4">
          <base-skeleton-loader class="tw-h-3 tw-w-16" />
        </td>
        <td class="tw-py-3 tw-px-4">
          <base-skeleton-loader class="tw-h-3 tw-w-48" />
        </td>
        <td class="tw-py-3 tw-px-4">
          <base-skeleton-loader class="tw-h-3 tw-w-48" />
        </td>
        <td class="tw-py-3 tw-px-4">
          <base-skeleton-loader class="tw-h-3 tw-w-48" />
        </td>
        <td class="tw-py-3 tw-px-4">
          <base-skeleton-loader class="tw-h-3 tw-w-48" />
        </td>
      </template>
    </base-table>
    <base-pagination v-model="page" class="tw-mt-2" />
    <report-filter-slideover
      ref="filterSlideOver"
      :business-id="businessId"
      date-filter-label="Charge Date"
      :show-programs-filter="false"
      :show-program-groups-filter="false"
      :show-prescription-status-filter="false"
      :show-plan-status-filter="false"
      @filter-updated="applyFilter"
    />
  </div>
</template>

<script>
import ReportFilterSlideover from "@/views/business/reports/components/ReportFilterSlideover";
import FilterDisplay from "@/views/business/reports/components/FilterDisplay";
import { FilterIcon } from "@/assets/svgs";
import { mapGetters } from "vuex";
import moment from "moment";
import { capitalize } from "lodash";
import ExportButton from "@/components/ExportButton.vue";
import MoneyFormat from "vue-money-format";

const TAKE_KEY = "predictile_charges_report_take";

export default {
  name: "BusinessMemberChargesReport",
  components: {
    ReportFilterSlideover,
    FilterDisplay,
    FilterIcon,
    ExportButton,
    MoneyFormat,
  },
  props: {
    businessId: String,
  },
  data: function() {
    return {
      charges: [],
      page: {
        take: localStorage.getItem(TAKE_KEY) ? +localStorage.getItem(TAKE_KEY) : 50,
        skip: 0,
        total: 0,
      },
      isLoading: false,
      filters: {
        startDate: null,
        endDate: null,
        locationsFilter: [],
        plansFilter: [],
        planGroupsFilter: [],
      },
      sortKey: "date",
      sortDesc: true,
      planGroupAggregates: [],
      businessPlanAggregates: [],
      locationAggregates: [],
      totalAmountInCents: 0,
      taxCollectedInCents: 0,
      totalCurrency: "usd",
    };
  },
  computed: {
    ...mapGetters("organization", ["organization", "cobrandId"]),
    locale() {
      return window.navigator.language;
    },
    showFilters() {
      if (
        this.filters.startDate !== null ||
        this.filters.endDate !== null ||
        this.filters.locationsFilter.length > 0 ||
        this.filters.plansFilter.length > 0 ||
        this.filters.planGroupsFilter.length > 0
      )
        return true;
      else return false;
    },
    fields() {
      let columns = [];
      columns.push({ title: "Date", key: "date", isSortable: true });
      columns.push({ title: "Amount", key: "amount" });
      columns.push({ title: "Status", key: "status", isSortable: true });
      columns.push({ title: "Description", key: "description" });
      columns.push({ title: "Location", key: "location", isSortable: true });
      columns.push({ title: "Plan", key: "plan", isSortable: true });
      columns.push({ title: "Group", key: "group", isSortable: true });

      return columns;
    },
    requestPayload() {
      let locationFilterStrings = this.filters.locationsFilter
        ? this.filters.locationsFilter.map(function(item) {
            return item["businessLocationId"];
          })
        : [];
      let planFilterStrings = this.filters.plansFilter
        ? this.filters.plansFilter.map(function(item) {
            return item["businessPlanId"];
          })
        : [];

      return {
        startDate: this.filters.startDate,
        endDate: this.filters.endDate,
        businessLocationIds: locationFilterStrings,
        planGroups: this.filters.planGroupsFilter,
        businessPlanIds: planFilterStrings,
        sortColumn: this.sortKey,
        sortOrder: this.sortDesc === true ? "desc" : "asc",
      };
    },
  },
  watch: {
    page(newValue) {
      localStorage.setItem(TAKE_KEY, newValue.take);
      this.loadPage();
    },
    sortKey() {
      this.loadPage();
    },
    sortDesc() {
      this.loadPage();
    },
  },
  mounted() {
    this.$refs.filterSlideOver.dateShortcutSelected("month");
    this.$refs.filterSlideOver.applyFilter();
  },
  methods: {
    didErrorExport(err) {
      this.EventBus.$emit("GLOBAL_ERROR", {
        title: "Failed to download CSV",
        message: "We had a problem downloading the report. Please reload the page to try again.",
        error: err,
      });
    },
    statusColor(status) {
      switch (status) {
        case "succeeded":
          return "green";
        case "failed":
          return "red";
        default:
          return "gray";
      }
    },
    capitalize,
    openFilters() {
      this.$refs.filterSlideOver.show();
    },
    applyFilter(filters) {
      this.filters = filters;
      this.loadPage();
    },
    next() {
      this.page.skip += this.page.take;
    },
    previous() {
      this.page.skip -= this.page.take;
    },
    loadPage() {
      this.isLoading = true;
      let url = `/businesses/${this.businessId}/reports/charges/search?skip=${this.page.skip}&take=${this.page.take}`;

      this.$axios
        .post(url, this.requestPayload)
        .then((res) => {
          this.charges = res.data.page.items;
          this.page.total = res.data.page.total;
          this.planGroupAggregates = res.data.groupRevenue;
          this.businessPlanAggregates = res.data.planRevenue;
          this.locationAggregates = res.data.locationRevenue;
          this.totalAmountInCents = res.data.totalAmountInCents;
          this.taxCollectedInCents = res.data.taxCollectedInCents;
        })
        .catch((err) => {
          this.EventBus.$emit("GLOBAL_ERROR", {
            title: "Failed to load charges",
            message: "We had a problem retrieving the list of charges. Please reload the page to try again.",
            error: err,
          });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    formatDate(date) {
      return new moment.utc(date).local().format("M/D/YYYY h:mm a");
    },
    view(userId) {
      window.open(`/businesses/${this.businessId}/members/${userId}/history`, "_blank");
    },
  },
};
</script>
<style scoped>
.summarylist {
  max-height: 247px;
}
</style>
